import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useJobsData } from '@pages/Transpiler/useJobsData';
import { ArrowNarrowUpRightIcon, EditBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Table, TableActions, TableIconButton } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';

import { JobInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, hasStatusWithError, InfoTableHeader, StyledNameContainer } from '../util';

const jobColumns: ColumnsType<JobInfoType> = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    filter: {
      type: 'search',
      searchInputProps: {
        placeholder: 'Search..',
        'data-test-id': 'jobs-table-name-search-input'
      }
    },
    width: '20%',
    render: (name) => {
      return (
        <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
          <EntityIcon entity={Entity.Job} size='xs' />
          <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
        </Stack>
      );
    }
  },
  {
    key: 'source',
    dataIndex: 'source',
    title: 'Source File',
    sortable: true,
    render: renderColumnWithEllipsis
  },
  {
    key: 'fabric',
    sortable: true,
    dataIndex: 'fabric',
    title: 'Fabric',
    render: (name) => {
      return (
        <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
          <EntityIcon entity={Entity.Fabric} size='xs' />
          <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
        </Stack>
      );
    }
  },
  getStatusColumn(),
  {
    key: 'actions',
    dataIndex: 'actions',
    width: '50px',
    render: (_, row) => {
      const entityId = row.id;
      if (hasStatusWithError(row.status)) {
        return null;
      }
      return entityId ? (
        <TableActions gap={theme.spaces.x8} align='end'>
          <TableIconButton
            icon={<EditBIcon type='default' />}
            elementType='link'
            to={getIDEUrl({
              uid: entityId,
              entity: Entity.Job
            })}
            aria-label={`Edit ${row.name}`}
            data-test-id='jobs-table-edit-button'
          />
          <TableIconButton
            icon={<ArrowNarrowUpRightIcon type='default' />}
            elementType='link'
            to={Private_Routes.Job.home.getUrl({ uid: entityId })}
            aria-label={`View ${row.name} details`}
            data-test-id='jobs-table-view-details-button'
          />
        </TableActions>
      ) : null;
    }
  }
];

export const JobsTable = ({ isBulk, id }: { isBulk: boolean; id: string }) => {
  const { data, isError, isLoading } = useJobsData(id, isBulk);

  const jobs = useMemo(() => data?.data?.jobs || [], [data?.data]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title='Jobs' count={jobs.length} />
      <Stack grow='1'>
        <Table
          fixedHeader={true}
          height='100%'
          loading={isLoading}
          width='100%'
          virtualize={true}
          columns={jobColumns}
          rowKey={'rowKey'}
          data-test-id='imported-info-jobs-table'
          dataSource={jobs.map((job, i) => ({ ...job, rowKey: i }))}
        />
      </Stack>
    </Stack>
  );
};
